/**
 * Engine constants for the save editor, transcribed from the engine source.
 *
 * Every value here has a citation to the file it came from. Where community
 * documentation of SAVE.DAT disagrees with the engine, the engine wins: the
 * engine is the program that writes the file.
 *
 * Nothing in this module is inferred. If you add a constant, cite it.
 */

/** engine/src/stat_defs.h — SAVEABLE_STAT_COUNT (stats persisted in the proto). */
export const SAVEABLE_STAT_COUNT = 35;

/** engine/src/stat_defs.h — PRIMARY_STAT_COUNT (the SPECIAL seven). */
export const PRIMARY_STAT_COUNT = 7;

/** engine/src/stat_defs.h — PRIMARY_STAT_MIN / PRIMARY_STAT_MAX. */
export const PRIMARY_STAT_MIN = 1;
export const PRIMARY_STAT_MAX = 10;

/** engine/src/stat_defs.h — PC_LEVEL_MAX. */
export const PC_LEVEL_MAX = 99;

/** engine/src/skill_defs.h — SKILL_COUNT. */
export const SKILL_COUNT = 18;

/** engine/src/skill_defs.h — NUM_TAGGED_SKILLS. */
export const NUM_TAGGED_SKILLS = 4;

/** engine/src/skill.cc — SKILLS_MIN_RAW_POINTS (floor applied to negative raw points). */
export const SKILLS_MIN_RAW_POINTS = -128;

/** engine/src/perk_defs.h — PERK_COUNT. */
export const PERK_COUNT = 119;

/** engine/src/trait_defs.h — TRAIT_COUNT / TRAITS_MAX_SELECTED_COUNT. */
export const TRAIT_COUNT = 16;
export const TRAITS_MAX_SELECTED_COUNT = 2;

/** engine/src/proto_types.h — KILL_TYPE_DEFAULT_COUNT. */
export const KILL_TYPE_COUNT = 19;

/** engine/src/stat_defs.h — PC_STAT_COUNT. */
export const PC_STAT_COUNT = 5;

/**
 * engine/src/combat_ai.cc `aiPacketWrite` — one AI packet is 45 int32s:
 * 20 scalars + 2 * HIT_LOCATION_SPECIFIC_COUNT (8) message ranges + 6 enums +
 * AI_PACKET_CHEM_PRIMARY_DESIRE_COUNT (3) desires.
 */
export const AI_PACKET_WORDS = 45;

/** engine/src/loadsave.cc — LS_PREVIEW_WIDTH * LS_PREVIEW_HEIGHT. */
export const PREVIEW_WIDTH = 224;
export const PREVIEW_HEIGHT = 133;
export const PREVIEW_SIZE = PREVIEW_WIDTH * PREVIEW_HEIGHT;

/** engine/src/loadsave.cc — LOAD_SAVE_SIGNATURE and its fixed 24-byte field. */
export const SAVE_SIGNATURE = 'FALLOUT SAVE FILE';
export const SIGNATURE_FIELD_SIZE = 24;

/** engine/src/loadsave.cc `lsgLoadHeaderInSlot` — only 18 bytes are compared. */
export const SIGNATURE_COMPARE_LENGTH = 18;

export const CHARACTER_NAME_SIZE = 32;
export const DESCRIPTION_SIZE = 30;
export const MAP_FILE_NAME_SIZE = 16;

/**
 * Total header size written by `lsgSaveHeaderInSlot`:
 *   24 signature + 2 major + 2 minor + 1 release + 32 name + 30 description
 * + 6 wall-clock d/m/y + 4 wall-clock time + 6 game m/d/y + 4 game ticks
 * + 2 elevation + 2 map + 16 map file + 29792 preview + 128 zero trailer.
 */
export const HEADER_SIZE = 30051;

/** engine/src/loadsave.cc — the version the loader will accept (minor.major + release). */
export const SUPPORTED_VERSION_MINOR = 1;
export const SUPPORTED_VERSION_MAJOR = 2;
export const SUPPORTED_VERSION_RELEASE = 'R';

/** engine/src/combat_defs.h — COMBAT_STATE_IN_COMBAT. `isInCombat()` is `state & 1`. */
export const COMBAT_STATE_IN_COMBAT = 0x01;

/** engine/src/stat_defs.h — Stat enum, in declaration order. Index == stat id. */
export const STAT_NAMES = [
  'STRENGTH', 'PERCEPTION', 'ENDURANCE', 'CHARISMA', 'INTELLIGENCE', 'AGILITY', 'LUCK',
  'MAXIMUM_HIT_POINTS', 'MAXIMUM_ACTION_POINTS', 'ARMOR_CLASS', 'UNARMED_DAMAGE',
  'MELEE_DAMAGE', 'CARRY_WEIGHT', 'SEQUENCE', 'HEALING_RATE', 'CRITICAL_CHANCE',
  'BETTER_CRITICALS', 'DAMAGE_THRESHOLD', 'DAMAGE_THRESHOLD_LASER', 'DAMAGE_THRESHOLD_FIRE',
  'DAMAGE_THRESHOLD_PLASMA', 'DAMAGE_THRESHOLD_ELECTRICAL', 'DAMAGE_THRESHOLD_EMP',
  'DAMAGE_THRESHOLD_EXPLOSION', 'DAMAGE_RESISTANCE', 'DAMAGE_RESISTANCE_LASER',
  'DAMAGE_RESISTANCE_FIRE', 'DAMAGE_RESISTANCE_PLASMA', 'DAMAGE_RESISTANCE_ELECTRICAL',
  'DAMAGE_RESISTANCE_EMP', 'DAMAGE_RESISTANCE_EXPLOSION', 'RADIATION_RESISTANCE',
  'POISON_RESISTANCE', 'AGE', 'GENDER',
  // The three below are pseudo-stats: computed, never stored in baseStats/bonusStats.
  'CURRENT_HIT_POINTS', 'CURRENT_POISON_LEVEL', 'CURRENT_RADIATION_LEVEL',
] as const;

/**
 * engine/src/stat.cc `gStatDescriptions` — [minimum, maximum] per stat, in the
 * same order as STAT_NAMES. These are the bounds `critterGetStat` clamps to, so
 * writing outside them is pointless: the engine will clamp on read anyway.
 * INT_MAX is spelled 0x7fffffff.
 */
export const STAT_BOUNDS: ReadonlyArray<readonly [number, number]> = [
  [1, 10], [1, 10], [1, 10], [1, 10], [1, 10], [1, 10], [1, 10],
  [0, 999], [1, 99], [0, 999], [0, 0x7fffffff], [0, 500], [0, 999], [0, 60],
  [0, 30], [0, 100], [-60, 100],
  [0, 100], [0, 100], [0, 100], [0, 100], [0, 100], [0, 100], [0, 100],
  [0, 90], [0, 90], [0, 90], [0, 90], [0, 90], [0, 100], [0, 90],
  [0, 95], [0, 95], [16, 101], [0, 1],
  [0, 2000], [0, 2000], [0, 2000],
];

/** engine/src/skill_defs.h — Skill enum, in declaration order. */
export const SKILL_NAMES = [
  'SMALL_GUNS', 'BIG_GUNS', 'ENERGY_WEAPONS', 'UNARMED', 'MELEE_WEAPONS', 'THROWING',
  'FIRST_AID', 'DOCTOR', 'SNEAK', 'LOCKPICK', 'STEAL', 'TRAPS', 'SCIENCE', 'REPAIR',
  'SPEECH', 'BARTER', 'GAMBLING', 'OUTDOORSMAN',
] as const;

/**
 * engine/src/skill.cc `defaultSkillDescriptions` — the part of SkillDescription
 * `skillGetValue` reads, in SKILL_NAMES order. `stat1`/`stat2` are Stat ids and
 * -1 is STAT_INVALID; `skillsInitDefaults` gives each named stat a multiplier of
 * `statModifier` and every other stat zero.
 */
export type SkillDescription = {
  readonly defaultValue: number;
  readonly statModifier: number;
  readonly stat1: number;
  readonly stat2: number;
  readonly baseValueMult: number;
};

export const SKILL_DESCRIPTIONS: ReadonlyArray<SkillDescription> = [
  { defaultValue: 5, statModifier: 4, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 2, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 2, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 30, statModifier: 2, stat1: 5, stat2: 0, baseValueMult: 1 },
  { defaultValue: 20, statModifier: 2, stat1: 5, stat2: 0, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 4, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 2, stat1: 1, stat2: 4, baseValueMult: 1 },
  { defaultValue: 5, statModifier: 1, stat1: 1, stat2: 4, baseValueMult: 1 },
  { defaultValue: 5, statModifier: 3, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 10, statModifier: 1, stat1: 1, stat2: 5, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 3, stat1: 5, stat2: -1, baseValueMult: 1 },
  { defaultValue: 10, statModifier: 1, stat1: 1, stat2: 5, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 4, stat1: 4, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 3, stat1: 4, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 5, stat1: 3, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 4, stat1: 3, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 5, stat1: 6, stat2: -1, baseValueMult: 1 },
  { defaultValue: 0, statModifier: 2, stat1: 2, stat2: 4, baseValueMult: 1 },
];

/** engine/src/skill.cc — `tagSkillBonus`, `skillMaximum`, `SKILLS_MIN_VALUE`. */
export const TAG_SKILL_BONUS = 20;
export const SKILL_MAXIMUM = 300;
export const SKILLS_MIN_VALUE = -999;

/** engine/src/trait_defs.h — Trait enum, in declaration order. */
export const TRAIT_NAMES = [
  'FAST_METABOLISM', 'BRUISER', 'SMALL_FRAME', 'ONE_HANDER', 'FINESSE', 'KAMIKAZE',
  'HEAVY_HANDED', 'FAST_SHOT', 'BLOODY_MESS', 'JINXED', 'GOOD_NATURED', 'CHEM_RELIANT',
  'CHEM_RESISTANT', 'SEX_APPEAL', 'SKILLED', 'GIFTED',
] as const;

/** engine/src/proto_types.h — KillType enum, first KILL_TYPE_DEFAULT_COUNT entries. */
export const KILL_TYPE_NAMES = [
  'MAN', 'WOMAN', 'CHILD', 'SUPER_MUTANT', 'GHOUL', 'BRAHMIN', 'RADSCORPION', 'RAT',
  'FLOATER', 'CENTAUR', 'ROBOT', 'DOG', 'MANTIS', 'DEATH_CLAW', 'PLANT', 'GECKO',
  'ALIEN', 'GIANT_ANT', 'BIG_BAD_BOSS',
] as const;

/** engine/src/stat_defs.h — PcStat enum, in declaration order. */
export const PC_STAT_NAMES = [
  'UNSPENT_SKILL_POINTS', 'LEVEL', 'EXPERIENCE', 'REPUTATION', 'KARMA',
] as const;

/** engine/src/stat.cc `gPcStatDescriptions` — [minimum, maximum] per PC stat. */
export const PC_STAT_BOUNDS: ReadonlyArray<readonly [number, number]> = [
  [0, 0x7fffffff], [1, PC_LEVEL_MAX], [0, 0x7fffffff], [-20, 20], [0, 0x7fffffff],
];

/** engine/src/perk_defs.h — Perk enum, in declaration order. */
export const PERK_NAMES = [
  'AWARENESS', 'BONUS_HTH_ATTACKS', 'BONUS_HTH_DAMAGE', 'BONUS_MOVE', 'BONUS_RANGED_DAMAGE',
  'BONUS_RATE_OF_FIRE', 'EARLIER_SEQUENCE', 'FASTER_HEALING', 'MORE_CRITICALS',
  'NIGHT_VISION', 'PRESENCE', 'RAD_RESISTANCE', 'TOUGHNESS', 'STRONG_BACK', 'SHARPSHOOTER',
  'SILENT_RUNNING', 'SURVIVALIST', 'MASTER_TRADER', 'EDUCATED', 'HEALER', 'FORTUNE_FINDER',
  'BETTER_CRITICALS', 'EMPATHY', 'SLAYER', 'SNIPER', 'SILENT_DEATH', 'ACTION_BOY',
  'MENTAL_BLOCK', 'LIFEGIVER', 'DODGER', 'SNAKEATER', 'MR_FIXIT', 'MEDIC', 'MASTER_THIEF',
  'SPEAKER', 'HEAVE_HO', 'FRIENDLY_FOE', 'PICKPOCKET', 'GHOST', 'CULT_OF_PERSONALITY',
  'SCROUNGER', 'EXPLORER', 'FLOWER_CHILD', 'PATHFINDER', 'ANIMAL_FRIEND', 'SCOUT',
  'MYSTERIOUS_STRANGER', 'RANGER', 'QUICK_POCKETS', 'SMOOTH_TALKER', 'SWIFT_LEARNER',
  'TAG', 'MUTATE', 'NUKA_COLA_ADDICTION', 'BUFFOUT_ADDICTION', 'MENTATS_ADDICTION',
  'PSYCHO_ADDICTION', 'RADAWAY_ADDICTION', 'WEAPON_LONG_RANGE', 'WEAPON_ACCURATE',
  'WEAPON_PENETRATE', 'WEAPON_KNOCKBACK', 'POWERED_ARMOR', 'COMBAT_ARMOR',
  'WEAPON_SCOPE_RANGE', 'WEAPON_FAST_RELOAD', 'WEAPON_NIGHT_SIGHT', 'WEAPON_FLAMEBOY',
  'ARMOR_ADVANCED_I', 'ARMOR_ADVANCED_II', 'JET_ADDICTION', 'TRAGIC_ADDICTION',
  'ARMOR_CHARISMA', 'GECKO_SKINNING', 'DERMAL_IMPACT_ARMOR',
  'DERMAL_IMPACT_ASSAULT_ENHANCEMENT', 'PHOENIX_ARMOR_IMPLANTS',
  'PHOENIX_ASSAULT_ENHANCEMENT', 'VAULT_CITY_INOCULATIONS', 'ADRENALINE_RUSH',
  'CAUTIOUS_NATURE', 'COMPREHENSION', 'DEMOLITION_EXPERT', 'GAMBLER', 'GAIN_STRENGTH',
  'GAIN_PERCEPTION', 'GAIN_ENDURANCE', 'GAIN_CHARISMA', 'GAIN_INTELLIGENCE', 'GAIN_AGILITY',
  'GAIN_LUCK', 'HARMLESS', 'HERE_AND_NOW', 'HTH_EVADE', 'KAMA_SUTRA_MASTER', 'KARMA_BEACON',
  'LIGHT_STEP', 'LIVING_ANATOMY', 'MAGNETIC_PERSONALITY', 'NEGOTIATOR', 'PACK_RAT',
  'PYROMANIAC', 'QUICK_RECOVERY', 'SALESMAN', 'STONEWALL', 'THIEF', 'WEAPON_HANDLING',
  'VAULT_CITY_TRAINING', 'ALCOHOL_RAISED_HIT_POINTS', 'ALCOHOL_RAISED_HIT_POINTS_II',
  'ALCOHOL_LOWERED_HIT_POINTS', 'ALCOHOL_LOWERED_HIT_POINTS_II', 'AUTODOC_RAISED_HIT_POINTS',
  'AUTODOC_RAISED_HIT_POINTS_II', 'AUTODOC_LOWERED_HIT_POINTS',
  'AUTODOC_LOWERED_HIT_POINTS_II', 'EXPERT_EXCREMENT_EXPEDITOR', 'WEAPON_ENHANCED_KNOCKOUT',
  'JINXED',
] as const;

/**
 * engine/src/perk.cc — perk ranks are small. `perkAddEffect` refuses beyond the
 * perk's declared maximum rank (from PERK.MSG / perk data), which never exceeds
 * this in stock data. Used both as an edit bound and as a parser sanity check.
 */
export const MAX_PERK_RANK = 10;

/** Index of a stat/skill/perk/trait by name, or -1. */
function indexer(names: readonly string[]): (name: string) => number {
  const map = new Map(names.map((n, i) => [n, i]));
  return (name) => map.get(name.toUpperCase()) ?? -1;
}

export const statIndex = indexer(STAT_NAMES);
export const skillIndex = indexer(SKILL_NAMES);
export const perkIndex = indexer(PERK_NAMES);
export const traitIndex = indexer(TRAIT_NAMES);
export const pcStatIndex = indexer(PC_STAT_NAMES);
export const killTypeIndex = indexer(KILL_TYPE_NAMES);
